const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const TIMEOUT_MS = 15000;

async function fetchJson(path) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, { signal: ctrl.signal });
  } catch (err) {
    throw new Error(err.name === 'AbortError' ? 'Request timed out — is the API up?' : 'Network error — check your connection.');
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    // Surface the API's own error message when it sends one.
    let detail = null;
    try {
      const body = await res.json();
      detail = body?.detail;
    } catch { /* non-JSON error body */ }
    throw new Error(detail || `API error: ${res.status}`);
  }
  return res.json();
}

export const api = {
  // Stats
  getOverview: () => fetchJson('/stats/overview'),
  getMeta: (limit = 10) => fetchJson(`/stats/meta?limit=${limit}&min_battles=20`),
  getLeaderboard: (sortBy = 'trophies', limit = 50) =>
    fetchJson(`/stats/leaderboard?sort_by=${sortBy}&limit=${limit}`),

  // Brawlers
  getBrawlers: (sortBy = 'win_rate') =>
    fetchJson(`/brawlers?sort_by=${sortBy}&min_battles=10`),
  getBrawler: (id) => fetchJson(`/brawlers/${id}`),
  // Posts written by the publisher from the stats
  getPosts: (game) => fetchJson(`/posts?limit=50${game ? `&game=${game}` : ''}`),
  getPost: (slug) => fetchJson(`/posts/${encodeURIComponent(slug)}`),
  // What moved in the meta: the last 7 days, or one finished week (a Monday)
  getMetaReport: () => fetchJson('/reports/meta'),
  getMetaReportWeek: (week) => fetchJson(`/reports/meta/${encodeURIComponent(week)}`),
  getBrawlerMatchups: (id, limit = 4) =>
    fetchJson(`/brawlers/${id}/matchups?limit=${limit}&min_battles=3`),

  // Maps
  getMaps: (mode) =>
    fetchJson(`/maps${mode ? `?mode=${encodeURIComponent(mode)}` : ''}`),
  getModes: () => fetchJson('/maps/modes'),
  getMap: (name) => fetchJson(`/maps/${encodeURIComponent(name)}`),

  // Players
  getPlayers: (search, limit = 50, offset = 0) =>
    fetchJson(`/players?limit=${limit}&offset=${offset}${search ? `&search=${encodeURIComponent(search)}` : ''}`),
  getPlayer: (tag) => fetchJson(`/players/${encodeURIComponent(tag)}`),
  getPlayerBrawlers: (tag) => fetchJson(`/players/${encodeURIComponent(tag)}/brawlers`),
  getPlayerBattles: (tag, limit = 25) =>
    fetchJson(`/players/${encodeURIComponent(tag)}/battles?limit=${limit}`),

  // Clash Royale upgrade advisor
  getClashAdvice: (tag, scope = 'deck') =>
    fetchJson(`/clash/advise/${encodeURIComponent(tag)}?scope=${scope}&limit=150`),
  // Which hero to buy with Hero Coins (weekly report)
  getClashHeroes: () => fetchJson('/clash/heroes'),
  getClashEvos: () => fetchJson('/clash/evos'),
  // Which cards are meta, and which of those take skill (weekly report)
  getClashMeta: () => fetchJson('/clash/meta'),
  // Decks gaining play and card pairs that win more together (weekly report)
  getClashTrending: () => fetchJson('/clash/trending'),
  // Deck ideas: the weekly top decks, or picks around one player's cards
  getClashDecks: () => fetchJson('/clash/decks'),
  getClashPlayerDecks: (tag) => fetchJson(`/clash/decks/${encodeURIComponent(tag)}`),
};
